"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import SectionHeader from "@/components/ui/section-header";
import { buttonVariants } from "@/components/ui";
import { cn } from "@/lib/utils";
import { ROUTES } from "@/lib/constants";
import { Info } from "@/components/icons";

// German clone of src/components/sections/products/shared/proxy-comparison —
// same structure/data, translated. "Mobile carrier identity" on the English
// page became "mobile-spezifische IP-Identität" (not "Netzbetreiber"/"Carrier")
// per the project's banned-word policy (carrier -> ISP/Mobile).

type ComparisonVariant = "mobile" | "residential";

interface Props {
  variant: ComparisonVariant;
}

const COMPARISON_ROWS = [
  { label: "Typischer Preis", mobile: "Ab $2/GB", residential: "Ab $1/GB" },
  {
    label: "Geografisches Targeting",
    mobile: "Land-, Stadt- und ISP-Targeting",
    residential: "Land-, Stadt- und ISP-Targeting in 195+ Ländern",
  },
  {
    label: "Session-Verhalten",
    mobile: "Rotierend oder sticky",
    residential: "Rotierend oder sticky",
  },
];

const USE_CASE_ROW = {
  label: "Am besten für",
  mobile: [
    { text: "Mobile App-Tests" },
    { text: "Mobile Umgebungen" },
    { text: "Ad Verification mit mobilen IPs", href: "/use-cases/ad-verification/" },
  ],
  residential: [
    { text: "Web Scraping", href: "/use-cases/web-scraping" },
    { text: "Preisüberwachung", href: "/use-cases/price-monitoring" },
    { text: "SEO-Monitoring", href: "/use-cases/seo-monitoring" },
    { text: "Marktforschung", href: "/use-cases/market-research/" },
    { text: "KI-Datenerfassung", href: "/use-cases/ai-llm-training/" },
  ],
};

export default function ProxyComparisonDe({ variant }: Props) {
  const prefersReducedMotion = useReducedMotion();

  function renderUseCaseItems(items: { text: string; href?: string }[]) {
    return items.map((item, i) => (
      <span key={item.text}>
        {item.href ? (
          <Link href={item.href} className="underline-offset-4 hover:underline">
            {item.text}
          </Link>
        ) : (
          item.text
        )}
        {i < items.length - 1 && ", "}
      </span>
    ));
  }

  return (
    <section
      id="compare-proxies"
      className="py-section border-border border-b"
      aria-labelledby="compare-proxies-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.5 }}
        >
          <SectionHeader
            eyebrow="Proxy-Typen vergleichen?"
            id="compare-proxies-heading"
            heading={
              variant === "residential"
                ? "Residential Proxies vs. Mobile Proxies"
                : "Mobile vs. Residential Proxies"
            }
            body="Beide Proxy-Typen decken viele der gleichen Aufgaben ab. Mobile Proxies eignen sich am besten, wenn gezielt eine mobile IP-Identität benötigt wird, während Residential Proxies für skalierbares Web Scraping und Datenerfassung meist kostengünstiger sind."
          />

          <div className="border-border mt-10 overflow-x-auto rounded-xl border">
            <table className="w-full min-w-[560px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-border bg-foreground/[0.03] border-b">
                  <th scope="col" className="text-muted-foreground px-5 py-3.5 font-medium">
                    Kriterium
                  </th>
                  <th scope="col" className="text-foreground px-5 py-3.5 font-semibold">
                    Mobile Proxies
                  </th>
                  <th scope="col" className="text-foreground px-5 py-3.5 font-semibold">
                    Residential Proxies
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-border bg-foreground/[0.03] border-b">
                  <th
                    scope="row"
                    className="text-muted-foreground px-5 py-3.5 font-medium whitespace-nowrap"
                  >
                    {USE_CASE_ROW.label}
                  </th>
                  <td className="text-foreground/90 px-5 py-3.5">
                    {renderUseCaseItems(USE_CASE_ROW.mobile)}
                  </td>
                  <td className="text-foreground/90 px-5 py-3.5">
                    {renderUseCaseItems(USE_CASE_ROW.residential)}
                  </td>
                </tr>
                {COMPARISON_ROWS.map((row) => (
                  <tr key={row.label} className="border-border border-b last:border-b-0">
                    <th
                      scope="row"
                      className="text-muted-foreground px-5 py-3.5 font-medium whitespace-nowrap"
                    >
                      {row.label}
                    </th>
                    <td className="text-foreground/90 px-5 py-3.5">{row.mobile}</td>
                    <td className="text-foreground/90 px-5 py-3.5">{row.residential}</td>
                  </tr>
                ))}
                <tr className="bg-foreground/[0.03]">
                  <th
                    scope="row"
                    className="text-muted-foreground px-5 py-4 font-medium whitespace-nowrap"
                  >
                    Preise
                  </th>
                  <td className="px-5 py-4">
                    <Link
                      href={ROUTES.pricingMobileProxies}
                      className={cn(buttonVariants({ variant: "outline", size: "sm" }))}
                    >
                      Mobile-Preise ansehen
                    </Link>
                  </td>
                  <td className="px-5 py-4">
                    <Link
                      href={ROUTES.pricingResidentialProxies}
                      className={cn(buttonVariants({ variant: "outline", size: "sm" }))}
                    >
                      Residential-Preise ansehen
                    </Link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="border-border bg-card mt-6 flex items-start gap-3 rounded-xl border p-5">
            <Info size={18} className="text-muted-foreground mt-0.5 shrink-0" aria-hidden="true" />
            <div>
              <h3 className="text-foreground text-sm font-semibold">Planen Sie Web Scraping?</h3>
              <p className="text-muted-foreground mt-1.5 text-sm leading-relaxed">
                Mobile Proxies lassen sich für Web Scraping einsetzen, aber Residential Proxies sind
                für umfangreiches Scraping, Preisüberwachung, SEO-Monitoring und KI-Datenerfassung
                meist die kostengünstigere Wahl. Mobile Proxies eignen sich, wenn das Ziel gezielt
                Datenverkehr aus einem mobilen Netz erfordert.
              </p>
            </div>
          </div>

          <p className="text-muted-foreground mt-6 text-center text-sm">
            Immer noch unsicher, was passt?{" "}
            <Link
              href={ROUTES.contactSupport}
              className="text-foreground font-medium underline underline-offset-4 transition-opacity hover:opacity-80"
            >
              Support kontaktieren
            </Link>{" "}
            oder den Live-Chat starten — wir helfen bei der Entscheidung.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
